import {
  ArrowDown,
  ArrowUp,
  ClipboardCheck,
  Play,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CreateAssessmentDialog } from "@/components/assessments/CreateAssessmentDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ContextBanner } from "@/components/common/ContextBanner";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { RiskScoreCircle } from "@/components/common/RiskScoreCircle";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/Button";
import { InputGroup } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";
import type { AssessmentStatus, Criticality, EntityStatus, Language, RiskLevel } from "@/types";

// ─────────────────────────────────────────────
// EntityDetail — Screen 4 (dQRqo)
//
// Single entity detail with metadata, dependencies,
// risk score, and recent assessments.
// Design: §8.5 Screen 4
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type EntityType = "APPLICATION" | "BUSINESS_PROCESS" | "HARDWARE" | "PERSON";
type DependencyType = "RUNTIME" | "DATA" | "AUTH" | "INTEGRATION";
type DataClassification = "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED";
type ApplicationType = "WEB_APPLICATION" | "MOBILE_APPLICATION" | "DESKTOP_APPLICATION" | "API" | "SERVICE" | "OTHER";
type DevelopmentType = "IN_HOUSE" | "VENDOR" | "OPEN_SOURCE" | "HYBRID" | string;
type HostingType = "ON_PREMISE" | "PUBLIC_CLOUD" | "PRIVATE_CLOUD" | "HYBRID_CLOUD" | string;

interface EntityDetail {
  id: string;
  name: string;
  type: EntityType;
  description?: string;
  version?: string;
  status: EntityStatus;
  businessSponsor?: string;
  itmProjectManager?: string;
  cisoName?: string;
  department?: string;
  businessCriticality?: Criticality;
  dataClassification?: DataClassification;
  applicationType?: ApplicationType;
  development?: DevelopmentType;
  hosting?: HostingType;
  initialProductionDate?: string;
  lastArchitectureReview?: string;
  latestRiskScore?: number;
  latestRiskLevel?: RiskLevel;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface Dependency {
  id: string;
  dependentId: string;
  dependencyId: string;
  dependencyType: DependencyType;
  criticality?: Criticality;
  dependent?: { id: string; name: string; type: EntityType };
  dependency?: { id: string; name: string; type: EntityType };
}

interface AssessmentSchemaVersion {
  id: string;
  version: number;
  schemaId: string;
  schema?: { name: string; slug?: string };
}

interface Assessment {
  id: string;
  name?: string;
  currentVersion?: number;
  schemaVersion?: AssessmentSchemaVersion | null;
  status: AssessmentStatus;
  // S-139: the list endpoint returns the latest deterministic score here. This
  // was previously read from non-existent top-level score/riskLevel, so the
  // Score column always rendered "—".
  latestScore?: { overallScore: number; overallLevel: RiskLevel } | null;
  createdAt: string;
  updatedAt: string;
}

// ── Application Details edit form types ──────

type BusinessCriticalityValue = "LOW" | "MEDIUM" | "HIGH" | "NA" | "";
type DataClassificationValue = "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED" | "NA" | "";
type ApplicationTypeValue =
  | "WEB_APPLICATION"
  | "INFRASTRUCTURE"
  | "API"
  | "INTERNAL_TOOLING"
  | "MOBILE_APPLICATION"
  | "DESKTOP_APPLICATION"
  | "OTHER"
  | "";
type DevelopmentValue = "IN_HOUSE" | "HYBRID" | "VENDOR" | "";
type HostingValue = "ON_PREMISE" | "PRIVATE_CLOUD" | "PUBLIC_CLOUD" | "HYBRID" | "NA" | "";

interface DetailsForm {
  name: string;
  businessSponsor: string;
  itmProjectManager: string;
  cisoName: string;
  department: string;
  businessCriticality: BusinessCriticalityValue;
  dataClassification: DataClassificationValue;
  applicationType: ApplicationTypeValue;
  development: DevelopmentValue;
  hosting: HostingValue;
}

// Display labels for the Application Type select. Built from `t` inside the
// component (not module-level) since the text is user-visible and must
// follow the current locale — mirrors buildColumns(t) in AppInventory.tsx.
function buildApplicationTypeLabels(
  t: (key: string) => string
): Record<Exclude<ApplicationTypeValue, "">, string> {
  return {
    WEB_APPLICATION: t("entity.applicationTypes.WEB_APPLICATION"),
    INFRASTRUCTURE: t("entity.applicationTypes.INFRASTRUCTURE"),
    API: t("entity.applicationTypes.API"),
    INTERNAL_TOOLING: t("entity.applicationTypes.INTERNAL_TOOLING"),
    MOBILE_APPLICATION: t("entity.applicationTypes.MOBILE_APPLICATION"),
    DESKTOP_APPLICATION: t("entity.applicationTypes.DESKTOP_APPLICATION"),
    OTHER: t("entity.applicationTypes.OTHER"),
  };
}

// ── Entity type badge ─────────────────────────

function EntityTypeBadge({ type }: { type: EntityType }) {
  const { t } = useTranslation();
  const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
    APPLICATION: t("entity.types.APPLICATION"),
    BUSINESS_PROCESS: t("entity.types.BUSINESS_PROCESS"),
    HARDWARE: t("entity.types.HARDWARE"),
    PERSON: t("entity.types.PERSON"),
  };
  const ENTITY_TYPE_CLASSES: Record<EntityType, string> = {
    APPLICATION: "bg-accent-bg text-accent-text",
    BUSINESS_PROCESS: "bg-warning-bg text-warning",
    HARDWARE: "bg-k-purple-bg text-k-purple",
    PERSON: "bg-success-bg text-success",
  };
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold leading-none ${ENTITY_TYPE_CLASSES[type] ?? "bg-hover text-text-muted"}`}
    >
      {ENTITY_TYPE_LABELS[type] ?? type}
    </span>
  );
}

function DependencyTypeBadge({ type }: { type: DependencyType }) {
  const { t } = useTranslation();
  const DEP_TYPE_LABELS: Record<DependencyType, string> = {
    RUNTIME: t("entity.dependencyTypes.RUNTIME"),
    DATA: t("entity.dependencyTypes.DATA"),
    AUTH: t("entity.dependencyTypes.AUTH"),
    INTEGRATION: t("entity.dependencyTypes.INTEGRATION"),
  };
  return (
    <span className="inline-flex items-center rounded-badge bg-hover px-2 py-0.5 text-[12px] font-semibold leading-none text-text-muted">
      {DEP_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ── Metadata row ──────────────────────────────

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption font-medium text-text-muted">{label}</dt>
      <dd className="text-body text-text-secondary">{value ?? "-"}</dd>
    </div>
  );
}

// ── Assessment columns ────────────────────────

function buildAssessmentColumns(t: (key: string) => string, language: Language): ColumnDef<Assessment>[] {
  return [
    {
      key: "schemaName",
      header: t("assessment.schema"),
      cell: (row) => (
        <span className="font-medium text-text-primary">
          {row.schemaVersion?.schema?.name ?? "-"}
        </span>
      ),
    },
    {
      key: "schemaVersionNumber",
      header: t("assessment.version"),
      cell: (row) => (
        <span className="font-mono text-caption text-text-muted">
          {row.currentVersion !== undefined && row.currentVersion !== null
            ? `v${row.currentVersion}`
            : "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: t("assessment.status"),
      cell: (row) => <StatusBadge type="assessment" status={row.status} />,
    },
    {
      key: "score",
      header: t("assessment.score"),
      cell: (row) => {
        const score = row.latestScore?.overallScore;
        const level = row.latestScore?.overallLevel;
        if (score === null || score === undefined)
          return <span className="text-text-muted">-</span>;
        const colors: Record<RiskLevel, string> = {
          LOW: "var(--success)",
          MEDIUM: "var(--warning)",
          HIGH: "var(--k-orange)",
          VERY_HIGH: "var(--k-purple)",
          CRITICAL: "var(--danger)",
        };
        const color = level ? colors[level] : "var(--accent)";
        const pct = Math.round(score);
        return (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-hover">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-caption text-text-muted">{pct}%</span>
          </div>
        );
      },
    },
    {
      key: "updatedAt",
      header: t("assessment.lastUpdated"),
      sortable: true,
      sortAccessor: (row) => new Date(row.updatedAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatDate(row.updatedAt, language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
  ];
}

// ── Main component ────────────────────────────

export function EntityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [entity, setEntity] = useState<EntityDetail | null>(null);
  const [upstream, setUpstream] = useState<Dependency[]>([]);
  const [downstream, setDownstream] = useState<Dependency[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailsForm, setDetailsForm] = useState<DetailsForm>({
    name: "",
    businessSponsor: "",
    itmProjectManager: "",
    cisoName: "",
    department: "",
    businessCriticality: "",
    dataClassification: "",
    applicationType: "",
    development: "",
    hosting: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function fetchAll() {
      setIsLoading(true);
      try {
        const [entityRes, depsRes, assessmentsRes] = await Promise.allSettled([
          api.get<{ data: EntityDetail }>(`/entities/${id}`),
          api.get<{ data: { upstream: Dependency[]; downstream: Dependency[] } }>(`/entities/${id}/dependencies`),
          api.get<{ data: Assessment[] }>(`/assessments?entityId=${id}&pageSize=10`),
        ]);

        if (cancelled) return;

        if (entityRes.status === "fulfilled") {
          const e = entityRes.value.data;
          setEntity(e);
          // S-124: businessSponsor / itmProjectManager / cisoName live in the
          // metadata JSON, not as top-level columns. Read them from metadata
          // (top-level kept only as a defensive fallback) so a saved value
          // actually reloads into the form instead of always showing blank.
          const meta = (e.metadata ?? {}) as Record<string, unknown>;
          const fromMeta = (key: string, fallback?: string) =>
            typeof meta[key] === "string" ? (meta[key] as string) : fallback ?? "";
          setDetailsForm({
            name: e.name ?? "",
            businessSponsor: fromMeta("businessSponsor", e.businessSponsor),
            itmProjectManager: fromMeta("itmProjectManager", e.itmProjectManager),
            cisoName: fromMeta("cisoName", e.cisoName),
            department: e.department ?? "",
            businessCriticality: (e.businessCriticality ?? "") as BusinessCriticalityValue,
            dataClassification: (e.dataClassification ?? "") as DataClassificationValue,
            applicationType: (e.applicationType ?? "") as ApplicationTypeValue,
            development: (e.development ?? "") as DevelopmentValue,
            hosting: (e.hosting ?? "") as HostingValue,
          });
        }
        if (depsRes.status === "fulfilled") {
          setUpstream(depsRes.value.data?.upstream ?? []);
          setDownstream(depsRes.value.data?.downstream ?? []);
        }
        if (assessmentsRes.status === "fulfilled")
          setAssessments(
            (assessmentsRes.value as { data: Assessment[] }).data ?? []
          );
      } catch {
        // handled per-request above
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/entities/${id}`);
      toast.success(t("entity.deleteDialog.successTitle"), `"${entity?.name}" ${t("entity.deleteDialog.deletedSuffix")}`);
      setDeleteOpen(false);
      navigate("/apps");
    } catch (err) {
      toast.error(
        t("entity.deleteDialog.failTitle"),
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleSaveDetails() {
    if (!id) return;
    setIsSaving(true);
    try {
      // S-124: metadata fields this form manages. Send them explicitly — even
      // when cleared (as "") — so an edit that empties a field actually
      // persists. The API merges this patch onto the entity's existing
      // metadata, so keys this form does NOT manage (description, version, …)
      // are preserved rather than wiped.
      const metadata: Record<string, string> = {
        businessSponsor: detailsForm.businessSponsor.trim(),
        itmProjectManager: detailsForm.itmProjectManager.trim(),
        cisoName: detailsForm.cisoName.trim(),
      };

      // Only include top-level fields the schema accepts; omit empty enum fields entirely
      const body: Record<string, unknown> = {
        ...(detailsForm.name.trim() ? { name: detailsForm.name.trim() } : {}),
        metadata,
        ...(detailsForm.department.trim() ? { department: detailsForm.department.trim() } : {}),
        ...(detailsForm.businessCriticality ? { businessCriticality: detailsForm.businessCriticality } : {}),
        ...(detailsForm.dataClassification ? { dataClassification: detailsForm.dataClassification } : {}),
        ...(detailsForm.applicationType ? { applicationType: detailsForm.applicationType } : {}),
        ...(detailsForm.development ? { development: detailsForm.development } : {}),
        ...(detailsForm.hosting ? { hosting: detailsForm.hosting } : {}),
      };
      const res = await api.patch<{ data: EntityDetail }>(`/entities/${id}`, body);
      setEntity(res.data);
      toast.success(t("entity.saveDetailsSuccessTitle"), t("entity.saveDetailsSuccessDesc"));
    } catch (err) {
      toast.error(
        t("entity.saveDetailsFailTitle"),
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setIsSaving(false);
    }
  }

  const assessmentColumns = buildAssessmentColumns(t, language);
  const APPLICATION_TYPE_LABELS = buildApplicationTypeLabels(t);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32 text-text-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-32">
        <p className="text-body text-text-muted">{t("entity.notFound")}</p>
        <Button variant="outline" onClick={() => navigate("/apps")}>
          {t("entity.backToInventory")}
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Archive banner */}
      {entity.status === "ARCHIVED" && (
        <ContextBanner
          variant="archived"
          title={t("status.archived")}
          message={t("entity.archivedBanner")}
          dismissible={false}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-semibold text-text-primary">
              {entity.name}
            </h1>
            <EntityTypeBadge type={entity.type} />
            <StatusBadge type="entity" status={entity.status} />
            {entity.latestRiskLevel && (
              <StatusBadge type="risk" status={entity.latestRiskLevel} />
            )}
          </div>
          {((entity.metadata as any)?.description ?? entity.description) && (
            <p className="text-body text-text-secondary">
              {(entity.metadata as any)?.description ?? entity.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            leadingIcon={<Trash2 className="h-4 w-4" />}
            onClick={() => setDeleteOpen(true)}
            disabled={entity.status === "ARCHIVED"}
          >
            {t("common.delete")}
          </Button>
          <Button
            leadingIcon={<Play className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
          >
            {t("assessment.startAssessment")}
          </Button>
        </div>
      </div>

      {/* Main body */}
      <div className="grid grid-cols-5 gap-4">
        {/* Details card (~60%) */}
        <div className="col-span-3 rounded-card border border-border-default bg-card p-5">
          <h2 className="mb-4 text-[16px] font-medium text-text-primary">
            {t("entity.applicationDetailsHeading")}
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {/* Name — full width */}
            <div className="col-span-2">
              <InputGroup
                label={t("entity.applicationName")}
                value={detailsForm.name}
                onChange={(e) => setDetailsForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("entity.applicationNamePlaceholder")}
                disabled={entity.status === "ARCHIVED"}
              />
            </div>

            {/* Text inputs */}
            <InputGroup
              label={t("entity.businessSponsor")}
              value={detailsForm.businessSponsor}
              onChange={(e) => setDetailsForm((f) => ({ ...f, businessSponsor: e.target.value }))}
              placeholder={t("entity.personNamePlaceholder")}
              disabled={entity.status === "ARCHIVED"}
            />
            <InputGroup
              label={t("entity.itmProjectManager")}
              value={detailsForm.itmProjectManager}
              onChange={(e) => setDetailsForm((f) => ({ ...f, itmProjectManager: e.target.value }))}
              placeholder={t("entity.personNamePlaceholder")}
              disabled={entity.status === "ARCHIVED"}
            />
            <InputGroup
              label={t("entity.cisoName")}
              value={detailsForm.cisoName}
              onChange={(e) => setDetailsForm((f) => ({ ...f, cisoName: e.target.value }))}
              placeholder={t("entity.personNamePlaceholder")}
              disabled={entity.status === "ARCHIVED"}
            />
            <InputGroup
              label={t("entity.department")}
              value={detailsForm.department}
              onChange={(e) => setDetailsForm((f) => ({ ...f, department: e.target.value }))}
              placeholder={t("entity.departmentPlaceholder")}
              disabled={entity.status === "ARCHIVED"}
            />

            {/* Business Criticality */}
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("entity.businessCriticality")}
              </label>
              <SelectRoot
                value={detailsForm.businessCriticality}
                onValueChange={(v) => setDetailsForm((f) => ({ ...f, businessCriticality: v as BusinessCriticalityValue }))}
                disabled={entity.status === "ARCHIVED"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("entity.selectCriticalityPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">{t("risk.low")}</SelectItem>
                  <SelectItem value="MEDIUM">{t("risk.medium")}</SelectItem>
                  <SelectItem value="HIGH">{t("risk.high")}</SelectItem>
                  <SelectItem value="NA">{t("common.notApplicable")}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>

            {/* Data Classification */}
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("entity.dataClassification")}
              </label>
              <SelectRoot
                value={detailsForm.dataClassification}
                onValueChange={(v) => setDetailsForm((f) => ({ ...f, dataClassification: v as DataClassificationValue }))}
                disabled={entity.status === "ARCHIVED"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("entity.selectClassificationPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PUBLIC">{t("entity.dataClassificationOptions.PUBLIC")}</SelectItem>
                  <SelectItem value="INTERNAL">{t("entity.dataClassificationOptions.INTERNAL")}</SelectItem>
                  <SelectItem value="CONFIDENTIAL">{t("entity.dataClassificationOptions.CONFIDENTIAL")}</SelectItem>
                  <SelectItem value="RESTRICTED">{t("entity.dataClassificationOptions.RESTRICTED")}</SelectItem>
                  <SelectItem value="NA">{t("common.notApplicable")}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>

            {/* Application Type */}
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("entity.applicationType")}
              </label>
              <SelectRoot
                value={detailsForm.applicationType}
                onValueChange={(v) => setDetailsForm((f) => ({ ...f, applicationType: v as ApplicationTypeValue }))}
                disabled={entity.status === "ARCHIVED"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("entity.registerDialog.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(APPLICATION_TYPE_LABELS) as Array<Exclude<ApplicationTypeValue, "">>).map((k) => (
                    <SelectItem key={k} value={k}>{APPLICATION_TYPE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            {/* Development */}
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("entity.development")}
              </label>
              <SelectRoot
                value={detailsForm.development}
                onValueChange={(v) => setDetailsForm((f) => ({ ...f, development: v as DevelopmentValue }))}
                disabled={entity.status === "ARCHIVED"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("entity.selectDevelopmentPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN_HOUSE">{t("entity.developmentOptions.IN_HOUSE")}</SelectItem>
                  <SelectItem value="HYBRID">{t("entity.developmentOptions.HYBRID")}</SelectItem>
                  <SelectItem value="VENDOR">{t("entity.developmentOptions.VENDOR")}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>

            {/* Hosting */}
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">
                {t("entity.hosting")}
              </label>
              <SelectRoot
                value={detailsForm.hosting}
                onValueChange={(v) => setDetailsForm((f) => ({ ...f, hosting: v as HostingValue }))}
                disabled={entity.status === "ARCHIVED"}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("entity.selectHostingPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ON_PREMISE">{t("entity.hostingOptions.ON_PREMISE")}</SelectItem>
                  <SelectItem value="PRIVATE_CLOUD">{t("entity.hostingOptions.PRIVATE_CLOUD")}</SelectItem>
                  <SelectItem value="PUBLIC_CLOUD">{t("entity.hostingOptions.PUBLIC_CLOUD")}</SelectItem>
                  <SelectItem value="HYBRID">{t("entity.hostingOptions.HYBRID")}</SelectItem>
                  <SelectItem value="NA">{t("common.notApplicable")}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>

            {/* Read-only date fields */}
            <div className="flex flex-col gap-0.5">
              <dt className="text-caption font-medium text-text-muted">{t("entity.initialProductionDate")}</dt>
              <dd className="text-body text-text-secondary">
                {entity.initialProductionDate
                  ? formatDate(entity.initialProductionDate, language)
                  : "-"}
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-caption font-medium text-text-muted">{t("entity.lastArchitectureReview")}</dt>
              <dd className="text-body text-text-secondary">
                {entity.lastArchitectureReview
                  ? formatDate(entity.lastArchitectureReview, language)
                  : "-"}
              </dd>
            </div>
          </div>

          {/* Save button */}
          {entity.status !== "ARCHIVED" && (
            <div className="mt-5 flex justify-end">
              <Button
                onClick={handleSaveDetails}
                isLoading={isSaving}
              >
                {t("entity.saveDetails")}
              </Button>
            </div>
          )}
        </div>

        {/* Right column (~40%) */}
        <div className="col-span-2 flex flex-col gap-4">
          {/* Dependencies card */}
          <div className="rounded-card border border-border-default bg-card p-5">
            <h2 className="mb-4 text-[16px] font-medium text-text-primary">
              {t("entity.dependencies")}
            </h2>

            {/* Upstream */}
            <div className="mb-4">
              <div className="mb-2 flex items-center gap-2">
                <ArrowUp className="h-3.5 w-3.5 text-text-muted" />
                <span className="text-caption font-medium text-text-muted">
                  {t("entity.upstream")} ({upstream.length})
                </span>
              </div>
              {upstream.length === 0 ? (
                <p className="text-caption text-text-muted">{t("common.none")}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {upstream.map((dep) => (
                    <div
                      key={dep.id}
                      className="flex items-center justify-between rounded-control bg-hover px-3 py-2"
                    >
                      <span className="text-body text-text-secondary">
                        {dep.dependency?.name ?? dep.dependencyId}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <DependencyTypeBadge type={dep.dependencyType} />
                        {dep.criticality && (
                          <StatusBadge type="criticality" status={dep.criticality} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Downstream */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <ArrowDown className="h-3.5 w-3.5 text-text-muted" />
                <span className="text-caption font-medium text-text-muted">
                  {t("entity.downstream")} ({downstream.length})
                </span>
              </div>
              {downstream.length === 0 ? (
                <p className="text-caption text-text-muted">{t("common.none")}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {downstream.map((dep) => (
                    <div
                      key={dep.id}
                      className="flex items-center justify-between rounded-control bg-hover px-3 py-2"
                    >
                      <span className="text-body text-text-secondary">
                        {dep.dependent?.name ?? dep.dependentId}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <DependencyTypeBadge type={dep.dependencyType} />
                        {dep.criticality && (
                          <StatusBadge type="criticality" status={dep.criticality} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Risk Score card */}
          {entity.latestRiskScore !== null && entity.latestRiskScore !== undefined && entity.latestRiskLevel && (
            <div className="flex flex-col items-center gap-4 rounded-card border border-border-default bg-card p-5">
              <h2 className="self-start text-[16px] font-medium text-text-primary">
                {t("risk.score")}
              </h2>
              <RiskScoreCircle
                score={entity.latestRiskScore}
                riskLevel={entity.latestRiskLevel}
              />
            </div>
          )}
        </div>
      </div>

      {/* Recent Assessments */}
      <div className="flex flex-col gap-3">
        <h2 className="text-[16px] font-medium text-text-primary">
          {t("entity.recentAssessments")}
        </h2>
        <DataTable
          columns={assessmentColumns}
          data={assessments}
          isLoading={isLoading}
          onRowClick={(row) => navigate(`/assessments/${row.id}`)}
          emptyState={{
            icon: ClipboardCheck,
            title: t("reports.noAssessmentsYet"),
            description: t("entity.startAssessmentDesc"),
            action: {
              label: t("assessment.startAssessment"),
              onClick: () => setCreateOpen(true),
            },
          }}
        />
      </div>

      {/* Create assessment */}
      <CreateAssessmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultEntityId={entity.id}
        onCreated={(assessmentId) => navigate(`/assessments/${assessmentId}`)}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("entity.deleteDialog.title")}
        description={`${t("entity.deleteDialog.confirmPrefix")} "${entity.name}"${t("entity.deleteDialog.confirmSuffix")}`}
        confirmLabel={t("common.delete")}
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  );
}
